import { SearchIcon } from "lucide-react";
import { useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getFilteredRowModel,
  SortingState,
  getSortedRowModel,
} from "@tanstack/react-table";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { ScrollArea } from "@/components/ui/scroll-area";

import { IOffStreetParkingSummary } from "@/types";
import { DataTableProps } from "@/components/data-table/types/universal-data-table-props.type";

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
}: Pick<DataTableProps<TData, TValue>, "data" | "columns" | "onRowClick">) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const { name, address } = row.original as IOffStreetParkingSummary;
      const search = (filterValue as string).toLowerCase();
      return name.toLowerCase().includes(search) || address.toLowerCase().includes(search);
    },
    state: {
      globalFilter,
      sorting,
      pagination: {
        pageSize: data.length,
        pageIndex: 0,
      },
    },
  });

  return (
    <div>
      <div className="flex items-center py-4">
        <div className="flex h-9 items-center gap-2 px-3 w-full max-w-sm bg-primary text-white rounded-md">
          <SearchIcon className="size-4 shrink-0 opacity-50" />
          <input
            className="placeholder:text-muted-foreground flex h-10 w-full bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
            type="search"
            placeholder="Filtrer par nom ou adresse..."
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            maxLength={60}
          />
        </div>
      </div>
      <div className="rounded-md border bg-primary text-white h-[380px] w-68 overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
        </Table>
        <ScrollArea className="h-[calc(100%-40px)]">
          <Table>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="cursor-pointer border-b-white"
                    onClick={() => onRowClick && onRowClick(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
    </div>
  );
}
